import React, { useMemo } from "react";
import { formatCurrency } from "../../../../utils/financialMath";

/**
 * Bar Earnings Breakdown Component
 * Renders one container per bar with totals: base pay, cash tips, electronic tips, total.
 * Uses past earnings only (passed from parent).
 */
const BarEarningsBreakdown = ({ pastEarnings }) => {
  const { barTotals, grandTotals } = useMemo(() => {
    if (!pastEarnings || pastEarnings.length === 0) {
      return {
        barTotals: [],
        grandTotals: {
          showCount: 0,
          totalBasePay: 0,
          totalTipsCash: 0,
          totalTipsElectronic: 0,
          totalEarnings: 0,
        },
      };
    }

    const byBar = new Map();
    pastEarnings.forEach((e) => {
      const barId = e.bar_id ?? "unknown";
      const barName = e.bar_name || "Unknown Bar";
      if (!byBar.has(barId)) {
        byBar.set(barId, {
          bar_id: barId,
          bar_name: barName,
          showCount: 0,
          totalBasePay: 0,
          totalTipsCash: 0,
          totalTipsElectronic: 0,
        });
      }
      const row = byBar.get(barId);
      row.showCount += 1;
      row.totalBasePay += e.base_pay || 0;
      row.totalTipsCash += e.tips_cash || 0;
      row.totalTipsElectronic += e.tips_electronic || 0;
    });

    const bars = Array.from(byBar.values())
      .map((row) => ({
        ...row,
        totalEarnings:
          row.totalBasePay + row.totalTipsCash + row.totalTipsElectronic,
      }))
      .sort((a, b) => b.totalEarnings - a.totalEarnings);

    const grandTotals = bars.reduce(
      (acc, bar) => {
        acc.showCount += bar.showCount;
        acc.totalBasePay += bar.totalBasePay;
        acc.totalTipsCash += bar.totalTipsCash;
        acc.totalTipsElectronic += bar.totalTipsElectronic;
        acc.totalEarnings += bar.totalEarnings;
        return acc;
      },
      {
        showCount: 0,
        totalBasePay: 0,
        totalTipsCash: 0,
        totalTipsElectronic: 0,
        totalEarnings: 0,
      }
    );

    return { barTotals: bars, grandTotals };
  }, [pastEarnings]);

  if (barTotals.length === 0) {
    return (
      <div className="earnings-bar-breakdown-empty">
        <p>No bar earnings in this period.</p>
      </div>
    );
  }

  return (
    <div className="earnings-bar-breakdown">
      <div className="earnings-bar-breakdown-container">
        <table className="earnings-bar-table">
          <colgroup>
            <col className="earnings-bar-col-name" />
            <col className="earnings-bar-col-show-count" />
            <col className="earnings-bar-col-base-pay" />
            <col className="earnings-bar-col-cash-tips" />
            <col className="earnings-bar-col-electronic-tips" />
            <col className="earnings-bar-col-total" />
          </colgroup>
          <thead>
            <tr>
              <th>Bar Name</th>
              <th># of Shows</th>
              <th>Base Pay</th>
              <th>Cash Tips</th>
              <th>Electronic Tips</th>
              <th>Total</th>
            </tr>
          </thead>
          <tbody>
            {barTotals.map((bar, index) => (
              <tr key={bar.bar_id} className={index % 2 === 0 ? "even" : "odd"}>
                <td data-label="Bar Name">{bar.bar_name}</td>
                <td
                  className="earnings-bar-num earnings-bar-show-count"
                  data-label="# of Shows"
                >
                  {bar.showCount}
                </td>
                <td className="earnings-bar-num" data-label="Base Pay">
                  {formatCurrency(bar.totalBasePay)}
                </td>
                <td className="earnings-bar-num" data-label="Cash Tips">
                  {formatCurrency(bar.totalTipsCash)}
                </td>
                <td className="earnings-bar-num" data-label="Electronic Tips">
                  {formatCurrency(bar.totalTipsElectronic)}
                </td>
                <td
                  className="earnings-bar-num earnings-bar-total"
                  data-label="Total"
                >
                  {formatCurrency(bar.totalEarnings)}
                </td>
              </tr>
            ))}
            {/* Totals row at bottom */}
            <tr className="earnings-bar-totals-row">
              <td className="earnings-bar-mobile-summary-heading">TOTALS</td>
              <td
                className="earnings-bar-num earnings-bar-show-count"
                data-label="# of Shows"
              >
                {grandTotals.showCount}
              </td>
              <td className="earnings-bar-num" data-label="Base Pay">
                {formatCurrency(grandTotals.totalBasePay)}
              </td>
              <td className="earnings-bar-num" data-label="Cash Tips">
                {formatCurrency(grandTotals.totalTipsCash)}
              </td>
              <td className="earnings-bar-num" data-label="Electronic Tips">
                {formatCurrency(grandTotals.totalTipsElectronic)}
              </td>
              <td
                className="earnings-bar-num earnings-bar-total"
                data-label="Total"
              >
                {formatCurrency(grandTotals.totalEarnings)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default BarEarningsBreakdown;
